// FaceVerifyOverlay — explicit, two-stage face verification UI.
//
// Replaces the previous "click button → invisible video element →
// detect once after 800ms → maybe fail with 「無法偵測人臉」" flow,
// which operators reported as "feels like nothing's happening then
// it just fails". The new design:
//
//   Stage 1 — LIVENESS challenge (proves the camera sees a live human,
//   not a still photo). Server picks the action; UI renders the prompt
//   ("請眨眼" / "請微笑" / "請向左轉頭" / "請向右轉頭"). rAF loop
//   computes per-frame face landmarks + eye / mouth / head-pose metrics
//   and detects the action.
//
//   Stage 2 — FRONTAL capture (so the descriptor we compare against the
//   enrolment is in the same pose-family — frontal — to minimise false
//   rejects). Prompts "請正視鏡頭" and waits for yaw + pitch to settle
//   inside a small frontal window for N consecutive frames before
//   capturing.
//
//   Stage 3 — Submit + result. POST descriptor + photo to
//   /v1/patrols/scan/verify/face/finish. Backend compares against
//   enrolled descriptors; on pass, parent's onSuccess fires.
//
// Why two stages: the previous single-stage capture caught users at
// whatever pose they happened to be in. If the challenge was "turn
// left", capture happened at the extreme of the turn — far from the
// enrolled frontal, distance was higher, false rejects ensued. Splitting
// liveness proof from capture pose lets each stage be tuned for its
// purpose. Stage 1 is happy with any non-frontal pose (in fact for
// `turn_left` we WANT them looking left); Stage 2 requires frontal and
// stillness, the easiest pose for the descriptor net to embed cleanly.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, CheckCircle2, AlertTriangle, X, MapPin,
} from "lucide-react";
import { apiFetch } from "../../lib/api-client.js";

const FACEAPI_LIB_URL = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
const FACEAPI_MODELS_URL = "https://justadudewhohacks.github.io/face-api.js/models";

interface FaceApi {
  nets: {
    tinyFaceDetector: { loadFromUri(u: string): Promise<void> };
    faceLandmark68Net: { loadFromUri(u: string): Promise<void> };
    faceRecognitionNet: { loadFromUri(u: string): Promise<void> };
  };
  TinyFaceDetectorOptions: new (opts?: { inputSize?: number; scoreThreshold?: number }) => unknown;
  detectSingleFace(input: HTMLVideoElement | HTMLCanvasElement, opts?: unknown): {
    withFaceLandmarks(): { withFaceDescriptor(): Promise<{
      detection: { box: { x: number; y: number; width: number; height: number } };
      landmarks: { positions: { x: number; y: number }[] };
      descriptor: Float32Array;
    } | undefined> };
  };
}
declare global { interface Window { faceapi?: FaceApi } }

export type FaceAction = "blink" | "smile" | "turn_left" | "turn_right";

interface Props {
  /** scan_session token (header X-Scan-Session). */
  token: string;
  /** Challenge ID from /v1/patrols/scan/verify/face/challenge. */
  challengeId: string;
  /** Action the backend wants the user to perform. */
  action: FaceAction;
  /** Location hierarchy chip shown above the camera so the operator
   *  knows which location they're verifying for, even while
   *  the overlay covers the page underneath. All four are nullable
   *  for legacy / partial data. */
  locationBuilding: string | null;
  locationFloor: string | null;
  locationLabel: string | null;
  locationCode: string | null;
  /** Closes the overlay after the backend confirms a match. Parent
   *  should `reload()` the scan_session state to pick up the new
   *  officer_id. */
  onSuccess: () => void;
  /** Operator hit cancel OR the overlay encountered an unrecoverable
   *  error. Parent should hide the overlay; we already cleaned up
   *  the camera stream. */
  onCancel: () => void;
}

type Stage =
  | "loading"          // models / camera still spinning up
  | "liveness"         // doing the action
  | "frontal"          // returning to frontal for descriptor capture
  | "submitting"       // POSTing to /face/finish
  | "success"          // backend matched, brief celebration before onSuccess
  | "failed";          // unrecoverable; user must cancel or retry

/** Per-frame state tracked across the rAF loop. Lives in a ref so the
 *  effect closure isn't bound to a particular render's values. */
interface LivenessState {
  blinkLowSince: number | null;
  blinkCount: number;
  /** Adaptive baseline of this user's open-eye EAR. Initialised on the
   *  first frame and slowly raised toward higher samples; the blink
   *  detector derives its closed / open thresholds from this so users
   *  whose absolute EAR sits below 0.22 (narrow / mono-lid eyes) still
   *  produce a detectable closed→open transition. Null until first
   *  measurement. */
  earOpenMax: number | null;
  smileHoldFrames: number;
  turnHoldFrames: number;
  frontalHoldFrames: number;
  /** Per-user pose baseline for the frontal stage. The pitch estimator
   *  anchors on noseProgress=0.55 with a ×320 multiplier — Asian face
   *  proportions sit between 0.45 and 0.55 in "looking forward", so
   *  the calibrated pitch when genuinely frontal can already be ±30°.
   *  Collect the first ~15 frames once the operator enters frontal
   *  stage, take a running average, and judge subsequent frames as
   *  "frontal" relative to that personal baseline instead of an
   *  absolute zero. Reset to null on stage entry. */
  poseBaseline: { yaw: number; pitch: number; samples: number } | null;
}

export default function FaceVerifyOverlay(props: Props): React.ReactElement {
  const {
    token, challengeId, action,
    locationBuilding, locationFloor, locationLabel, locationCode,
    onSuccess, onCancel,
  } = props;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const livenessRef = useRef<LivenessState>({
    blinkLowSince: null, blinkCount: 0, earOpenMax: null,
    smileHoldFrames: 0, turnHoldFrames: 0, frontalHoldFrames: 0,
    poseBaseline: null,
  });
  const submittedRef = useRef(false);   // guards against double-submit
  // Sticky timestamp for the last successful face detection. Keeps the
  // overlay box + green guide oval steady through brief drops (blink
  // closures, mid-turn profile angles) instead of flickering "找不到人
  // 臉" between every detected frame.
  const lastDetectAtRef = useRef(0);

  const [stage, setStage] = useState<Stage>("loading");
  const [statusMsg, setStatusMsg] = useState<string>("正在載入人臉模型…");
  const [progress, setProgress] = useState(0);   // 0..1 — fills the ring around camera
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Mirror the last face-api detection so the operator gets a live "yes I
  // see you" rectangle overlaid on the camera. Null when the detector
  // returned nothing on the most recent frame — the guide oval flips
  // to a dimmer colour so they know to reframe.
  const [detBox, setDetBox] = useState<{
    box: { x: number; y: number; width: number; height: number };
    videoW: number;
    videoH: number;
  } | null>(null);

  // Mirror stage into a ref so the rAF loop reads the current value
  // without re-attaching on every state change (avoids glitch when the
  // stage transitions mid-frame).
  const stageRef = useRef(stage);
  useEffect(() => { stageRef.current = stage; }, [stage]);

  // ── Step 1: load face-api lib + models + camera ──────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (!window.faceapi) {
          await new Promise<void>((resolve, reject) => {
            const s = document.createElement("script");
            s.src = FACEAPI_LIB_URL; s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error("face-api.js 載入失敗"));
            document.head.appendChild(s);
          });
        }
        if (cancelled) return;
        setStatusMsg("正在載入辨識模型…");
        const fa = window.faceapi!;
        await Promise.all([
          fa.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODELS_URL),
          fa.nets.faceLandmark68Net.loadFromUri(FACEAPI_MODELS_URL),
          fa.nets.faceRecognitionNet.loadFromUri(FACEAPI_MODELS_URL),
        ]);
        if (cancelled) return;
        setStatusMsg("正在開啟相機…");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // iOS may block autoplay; the capture rAF loop polls
          // video.readyState so a quietly-rejected play() is fine.
          void video.play().catch(() => { /* */ });
        }
        setStage("liveness");
        setStatusMsg(actionInstruction(action));
      } catch (e) {
        if (cancelled) return;
        const err = e as Error & { name?: string };
        const msg = err.name === "NotAllowedError"
          ? "需要相機權限才能完成人臉辨識，請允許瀏覽器使用相機後再試。"
          : err.name === "NotFoundError" || err.name === "OverconstrainedError"
            ? "找不到可用的前置鏡頭，請改用裝置 Passkey。"
            : err.message || "初始化失敗，請再試一次。";
        setErrorMsg(msg);
        setStage("failed");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [action]);

  // ── Step 2: per-frame detect loop. Drives both stages plus the
  //           final capture + submit hand-off.
  useEffect(() => {
    if (stage === "loading" || stage === "failed" || stage === "success") return;
    if (!window.faceapi) return;
    const fa = window.faceapi;
    let stopped = false;
    let raf = 0;

    const tick = async () => {
      if (stopped) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2) {
        raf = requestAnimationFrame(tick);
        return;
      }
      try {
        // Detection threshold is stage-aware. tinyFaceDetector's score
        // drops sharply at non-frontal yaw / pitch (the model is
        // calibrated on frontal training data), so a 0.5 floor during
        // "turn head left/right/up/down" loses the face exactly when
        // we need to read landmarks. The post-detection liveness logic
        // already verifies pose against EAR / MAR / yaw thresholds, so
        // letting more weakly-scoring boxes through here is safe; the
        // bad ones get filtered downstream. In frontal stage we keep
        // 0.5 because that capture has to be high quality for the
        // descriptor comparison.
        // Liveness uses 0.15 because tinyFaceDetector scores drop hard
        // under uneven indoor lighting (red neon, hallway shadows). The
        // pose / EAR / MAR check downstream filters out genuinely-bad
        // boxes, so being permissive at the detector is safe. Frontal
        // capture still requires 0.5 because that descriptor needs to
        // be high quality.
        const threshold = stageRef.current === "liveness" ? 0.15 : 0.5;
        const det = await fa
          .detectSingleFace(video, new fa.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: threshold }))
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (stopped) return;
        const currentStage = stageRef.current;

        if (!det) {
          // Sticky window: if we saw a face within the last 500 ms,
          // suppress the "lost" UI. Blink closures, smile mouth-open,
          // and mid-turn profile angles routinely tank the detector
          // score for 100–300 ms; flipping the whole overlay and
          // status text on every such frame makes the screen look
          // broken even when the user is doing exactly what we asked.
          const sinceLast = Date.now() - lastDetectAtRef.current;
          if (sinceLast > 500) {
            setDetBox(null);
            // No face — give the operator a hint but don't penalise
            // (the liveness streak is intentionally not reset; a brief
            // miss shouldn't void accumulated progress). Stage-aware
            // copy: during liveness the operator is *deliberately*
            // turning / blinking / smiling, so a hardcoded「請正視鏡頭」
            // actively contradicts the action prompt above and confuses
            // them. Only ask for frontal view in the frontal-capture
            // stage.
            if (currentStage === "liveness") {
              setStatusMsg("找不到人臉，請將臉部對齊框內");
            } else {
              setStatusMsg("找不到人臉，請正視鏡頭");
            }
          }
          // Fall through to reschedule — early-returning here is what
          // historically killed the rAF loop the first time any frame
          // came back without a face: the bottom `requestAnimationFrame`
          // sits OUTSIDE the try block, and `return` inside the try
          // skipped right past it. Detection box would freeze on the
          // last good frame and never recover until the user navigated
          // away.
        } else {
          lastDetectAtRef.current = Date.now();
          setDetBox({
            box: det.detection.box,
            videoW: video.videoWidth,
            videoH: video.videoHeight,
          });

          if (currentStage === "liveness") {
            const r = updateLiveness(action, det.landmarks.positions, livenessRef.current);
            setStatusMsg(r.msg);
            setProgress(r.progress);
            if (r.passed) {
              setStage("frontal");
              setStatusMsg("請正視鏡頭");
              setProgress(0);
              livenessRef.current.frontalHoldFrames = 0;
              livenessRef.current.poseBaseline = null;   // re-bootstrap per user
            }
          } else if (currentStage === "frontal") {
            const { yaw, pitch } = estimatePose(det.landmarks.positions);
            // Bootstrap a per-user pose baseline over the first ~0.5 s
            // (15 frames). The pitch estimator's 0.55 nose-position
            // anchor is calibrated on Western face proportions; Asian
            // / mono-lid users frequently sit at noseProgress 0.45-0.50
            // even when looking dead ahead, which the absolute pitch
            // formula reads as -16° to -32°. Comparing against the
            // user's own "stable" pose instead of an absolute zero
            // removes the false reject.
            const baseline = livenessRef.current.poseBaseline;
            const BOOT_FRAMES = 15;
            if (!baseline) {
              livenessRef.current.poseBaseline = { yaw, pitch, samples: 1 };
            } else if (baseline.samples < BOOT_FRAMES) {
              baseline.yaw = (baseline.yaw * baseline.samples + yaw) / (baseline.samples + 1);
              baseline.pitch = (baseline.pitch * baseline.samples + pitch) / (baseline.samples + 1);
              baseline.samples += 1;
            }
            // While bootstrapping, accept any pose so the operator
            // doesn't see a fake "tilt your head" message during the
            // first half-second; we just collect samples. After the
            // baseline locks in, judge the current frame against it
            // PLUS a generous absolute envelope as a safety net.
            const ref = livenessRef.current.poseBaseline ?? { yaw: 0, pitch: 0, samples: 0 };
            const settled = ref.samples >= BOOT_FRAMES;
            const yawOk = settled
              ? Math.abs(yaw - ref.yaw) < 12 && Math.abs(yaw) < 25
              : true;
            const pitchOk = settled
              ? Math.abs(pitch - ref.pitch) < 15 && Math.abs(pitch) < 40
              : true;
            if (yawOk && pitchOk) {
              livenessRef.current.frontalHoldFrames += 1;
              const FRONTAL_FRAMES = 6;      // ~0.2s at 30 fps
              const p = Math.min(1, livenessRef.current.frontalHoldFrames / FRONTAL_FRAMES);
              setProgress(p);
              setStatusMsg("偵測到正面，請保持");
              if (livenessRef.current.frontalHoldFrames >= FRONTAL_FRAMES && !submittedRef.current) {
                submittedRef.current = true;
                stopped = true;     // stop loop; submit takes over
                await captureAndSubmit(det.descriptor, video, det.detection.box);
                return;
              }
            } else {
              // Soft decay rather than hard reset — a single noisy
              // frame (head bob, micro-tilt) shouldn't wipe out
              // accumulated frontal frames.
              livenessRef.current.frontalHoldFrames = Math.max(
                0,
                livenessRef.current.frontalHoldFrames - 1,
              );
              const FRONTAL_FRAMES = 6;
              setProgress(livenessRef.current.frontalHoldFrames / FRONTAL_FRAMES);
              setStatusMsg("請正視鏡頭");
            }
          }
        }
      } catch {
        // transient face-api hiccup; just retry next frame
      }
      if (!stopped) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => { stopped = true; cancelAnimationFrame(raf); };
    // captureAndSubmit + setX are stable / refs so we re-run only when
    // the stage transitions OUT of the running set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, action]);

  const captureAndSubmit = useCallback(async (
    descriptor: Float32Array,
    video: HTMLVideoElement,
    box: { x: number; y: number; width: number; height: number },
  ): Promise<void> => {
    setStage("submitting");
    setStatusMsg("比對中…");
    setProgress(0);
    try {
      const size = 320;
      const canvas = canvasRef.current ?? document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas context unavailable");
      // Mirror so the saved snapshot matches what the operator saw.
      ctx.translate(size, 0); ctx.scale(-1, 1);
      ctx.drawImage(video, box.x, box.y, box.width, box.height, 0, 0, size, size);
      const photoB64 = canvas.toDataURL("image/jpeg", 0.85);
      const r = await apiFetch("/v1/patrols/scan/verify/face/finish", {
        method: "POST",
        headers: { "x-scan-session": token, "content-type": "application/json" },
        body: JSON.stringify({
          challenge_id: challengeId,
          action,
          completed_at: Date.now(),
          descriptor: Array.from(descriptor),
          photo_b64: photoB64.replace(/^data:image\/[a-z]+;base64,/, ""),
        }),
      });
      const body = (await r.json().catch(() => ({}))) as {
        ok?: boolean; error?: string; distance?: number; match_score?: number;
      };
      if (!r.ok || !body.ok) {
        const msg = body.error === "no_face_enrolled"
          ? "您尚未註冊人臉樣本，請聯絡管理員。"
          : body.error === "challenge_expired"
            ? "活體挑戰已逾時，請重新嘗試人臉辨識。"
            : body.error === "challenge_already_used"
              ? "此次挑戰已使用過，請重新嘗試人臉辨識。"
              : (r.status === 401 && typeof body.distance === "number")
                ? "人臉不符合已註冊樣本，請正視鏡頭重試，或改用裝置 Passkey。"
                : `人臉驗證失敗：${body.error ?? r.status}`;
        setErrorMsg(msg);
        setStage("failed");
        return;
      }
      setStage("success");
      setStatusMsg("驗證成功！");
      setProgress(1);
      // brief celebration before handing back to the parent so the
      // operator gets a moment of "yes, that worked".
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setTimeout(() => onSuccess(), 700);
    } catch (e) {
      setErrorMsg((e as Error).message ?? "提交失敗，請重試。");
      setStage("failed");
    }
  }, [action, challengeId, token, onSuccess]);

  const cancel = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onCancel();
  }, [onCancel]);

  // ── UI ────────────────────────────────────────────────────────────────
  // Ring geometry: camera circle inset 12% (matches enroll page); the
  // SVG progress ring sits just outside, animated by the `progress` 0..1.
  const ringRadius = 47;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringDashOffset = ringCircumference * (1 - Math.min(1, Math.max(0, progress)));
  const ringStroke = stage === "success" ? "#22c55e"
                    : stage === "failed" ? "#ef4444"
                    : stage === "frontal" ? "#a78bfa"
                    : "#00d4ff";

  return (
    <div className="fixed inset-0 z-50 bg-bg-base/95 backdrop-blur-sm flex flex-col items-center px-6 py-8">
      <style>{`
        @keyframes face-verify-ring-pulse {
          0%, 100% { stroke-width: 2.6; }
          50%      { stroke-width: 3.4; }
        }
        @keyframes face-verify-success-pop {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>

      {/* Top bar — cancel + action prompt */}
      <div className="w-full max-w-md flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={cancel}
          className="rounded-full p-1.5 hover:bg-bg-raised text-text-secondary"
          aria-label="取消"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex-1 text-center">
          <p className="text-xs text-text-muted">人臉辨識</p>
          <p className="text-base font-semibold text-text-primary">
            {stage === "liveness"   ? actionInstruction(action)
             : stage === "frontal"   ? "請正視鏡頭"
             : stage === "submitting" ? "比對中"
             : stage === "success"   ? "驗證成功"
             : stage === "failed"    ? "驗證未通過"
             : "準備中"}
          </p>
        </div>
        {/* spacer to balance the X button so the prompt stays centred */}
        <div className="w-9" />
      </div>

      {/* Location pill — same 建築物 › 樓層 › 地點 chip as the page
          underneath, so the operator never loses sight of which
          location they're verifying for. Mirrors LocationBanner
          inline here to avoid an extra cross-module import. */}
      {(locationBuilding || locationFloor || locationLabel || locationCode) && (
        <div className="w-full max-w-md flex justify-center mb-4">
          <div className="inline-flex items-center flex-wrap gap-x-1.5 gap-y-0.5 rounded-full bg-bg-raised/60 border border-border-subtle px-3 py-1.5 max-w-full">
            <MapPin className="h-3.5 w-3.5 text-accent shrink-0" />
            {locationBuilding && (
              <span className="text-2xs text-text-muted">{locationBuilding}</span>
            )}
            {locationBuilding && locationFloor && (
              <span className="text-text-muted text-2xs">›</span>
            )}
            {locationFloor && (
              <span className="text-2xs text-text-muted">{locationFloor}</span>
            )}
            {(locationBuilding || locationFloor) && (locationLabel || locationCode) && (
              <span className="text-text-muted text-2xs">›</span>
            )}
            {(locationLabel || locationCode) && (
              <span className="text-xs font-medium text-text-primary">
                {locationLabel ?? locationCode}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Camera frame */}
      <div className="w-full max-w-md aspect-square relative">
        {/* Camera circle. Always mounted; opacity fades in once stream is ready. */}
        <div className="absolute rounded-full overflow-hidden bg-black"
             style={{ inset: "12%" }}>
          <video
            ref={videoRef}
            className={`w-full h-full object-cover scale-x-[-1] transition-opacity duration-300 ${
              stage === "loading" ? "opacity-0" : "opacity-100"
            }`}
            playsInline
            muted
          />
          {stage === "loading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <Loader2 className="h-7 w-7 animate-spin text-accent" />
              <p className="text-xs text-text-secondary">{statusMsg}</p>
            </div>
          )}
          {stage === "submitting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-bg-base/70 backdrop-blur-sm">
              <Loader2 className="h-7 w-7 animate-spin text-accent" />
              <p className="text-xs text-text-secondary">{statusMsg}</p>
            </div>
          )}
          {stage === "success" && (
            <div className="absolute inset-0 flex items-center justify-center bg-bg-base/60">
              <CheckCircle2
                className="h-16 w-16 text-success"
                style={{ animation: "face-verify-success-pop 350ms ease-out both" }}
              />
            </div>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {/* Progress ring just outside the camera circle, plus the face
            placement guide + live detection rectangle inside. All in one
            SVG sharing the wrapper's 100×100 viewBox so the coords stay
            in lockstep with the camera circle's 12% inset. */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full pointer-events-none"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* faint track */}
          <circle cx={50} cy={50} r={ringRadius}
            fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={2.6} />
          {/* live progress arc */}
          <circle cx={50} cy={50} r={ringRadius}
            fill="none"
            stroke={ringStroke}
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeDasharray={ringCircumference}
            strokeDashoffset={ringDashOffset}
            style={{
              transform: "rotate(-90deg)",
              transformOrigin: "50% 50%",
              transition: "stroke-dashoffset 200ms ease-out, stroke 250ms ease",
              filter: progress > 0 ? `drop-shadow(0 0 1.5px ${ringStroke})` : undefined,
              animation: stage === "liveness" || stage === "frontal"
                ? "face-verify-ring-pulse 1.4s ease-in-out infinite"
                : undefined,
            }}
          />
          {(stage === "liveness" || stage === "frontal") && (
            <>
              {/* Face placement guide — dashed oval sized to a typical
                  framed face. Goes green the moment the detector locks
                  on, otherwise stays dim white so the operator knows
                  exactly where to put their head. */}
              <ellipse
                cx={50} cy={50} rx={22} ry={28}
                fill="none"
                stroke={detBox ? "rgba(34, 197, 94, 0.7)" : "rgba(255, 255, 255, 0.35)"}
                strokeWidth={0.55}
                strokeDasharray="1.8 1.6"
                style={{ transition: "stroke 200ms ease" }}
              />
              {/* Live detection rectangle — maps video pixel coords to
                  the camera circle (object-cover crops the longer axis
                  to a square; scale-x-[-1] mirrors horizontally). */}
              {detBox && detBox.videoW > 0 && detBox.videoH > 0 && (() => {
                const { box, videoW, videoH } = detBox;
                const minDim = Math.min(videoW, videoH);
                const leftCrop = (videoW - minDim) / 2;
                const topCrop = (videoH - minDim) / 2;
                // Camera circle occupies 12..88 (76 units wide) in the
                // wrapper's 100×100 viewBox.
                const ux = 12 + ((box.x - leftCrop) / minDim) * 76;
                const uy = 12 + ((box.y - topCrop) / minDim) * 76;
                const uw = (box.width / minDim) * 76;
                const uh = (box.height / minDim) * 76;
                const uxM = 100 - (ux + uw);   // mirror to match scale-x-[-1]
                return (
                  <rect
                    x={uxM} y={uy} width={uw} height={uh}
                    fill="none"
                    stroke="rgba(34, 197, 94, 0.85)"
                    strokeWidth={0.45}
                    rx={1.2}
                  />
                );
              })()}
            </>
          )}
        </svg>
      </div>

      {/* Step progress — 3-step pipeline so the operator can see where
          they are in the verification flow. The single status line
          underneath was too easy to misread ("請正視鏡頭" felt like the
          end but is actually mid-flow). Order:
            1. 活體驗證  — the action prompt (blink / smile / turn)
            2. 正面取像  — capturing the frontal frame for comparison
            3. 比對中    — backend matching the descriptor */}
      <StepProgress stage={stage} />

      {/* Status text below the camera */}
      <div className="w-full max-w-md mt-4 min-h-[3rem] text-center">
        {stage === "failed" ? (
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 text-danger">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{errorMsg ?? "驗證未通過"}</span>
            </div>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={cancel}
                className="btn-secondary text-sm"
              >
                取消
              </button>
            </div>
          </div>
        ) : stage === "success" ? (
          <p className="text-sm text-success">{statusMsg}</p>
        ) : (
          <p className="text-sm text-text-secondary">{statusMsg}</p>
        )}
      </div>
    </div>
  );
}

// ── Liveness detectors ───────────────────────────────────────────────────

interface Point { x: number; y: number }

function dist(a: Point, b: Point): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

/**
 * EAR — Eye Aspect Ratio. Standard formula from Soukupová & Čech 2016.
 * Open eye ≈ 0.3–0.4, mid-blink ≈ 0.15, fully closed < 0.1.
 *
 * face-api 68-point landmarks: idx 36–41 is the subject's RIGHT eye in
 * the un-mirrored video; 42–47 is the LEFT eye. Mirroring on screen
 * doesn't change the landmark indices — those are tied to the
 * underlying image, not the displayed flip. We average both eyes to
 * make the blink detector robust to a single sticky eye.
 */
function avgEAR(lms: Point[]): number {
  const ear = (idx0: number) => {
    const p1 = lms[idx0 + 0];
    const p2 = lms[idx0 + 1];
    const p3 = lms[idx0 + 2];
    const p4 = lms[idx0 + 3];
    const p5 = lms[idx0 + 4];
    const p6 = lms[idx0 + 5];
    const A = dist(p2, p6);
    const B = dist(p3, p5);
    const C = dist(p1, p4);
    return (A + B) / (2 * Math.max(C, 1));
  };
  return (ear(36) + ear(42)) / 2;
}

/**
 * MAR-like ratio for smile detection. A relaxed mouth is roughly
 * "wide ≈ 2.5× tall"; smiling stretches the corners and the ratio
 * climbs to 3.5+. We hold-detect rather than spike-detect because
 * smiles often build up across a few frames.
 */
function mouthWidthOverHeight(lms: Point[]): number {
  // outer mouth: 48 (left corner) ↔ 54 (right corner)
  // vertical: 51 (upper lip) ↔ 57 (lower lip)
  const width = dist(lms[48], lms[54]);
  const height = dist(lms[51], lms[57]);
  return width / Math.max(height, 1);
}

/**
 * yaw/pitch in degrees (same estimator family as EnrollFacePage so the
 * scan + enrol pipelines agree on what "looking forward" means).
 */
function estimatePose(lms: Point[]): { yaw: number; pitch: number } {
  if (lms.length < 68) return { yaw: 0, pitch: 0 };
  const noseTip = lms[30];
  const leftEye = avg(lms.slice(36, 42));
  const rightEye = avg(lms.slice(42, 48));
  const chin = lms[8];
  const foreheadProxy = avg(lms.slice(17, 27));

  const eyeMidX = (leftEye.x + rightEye.x) / 2;
  const yawNorm = (noseTip.x - eyeMidX) / Math.max(1, Math.abs(rightEye.x - leftEye.x));
  // Flip yaw sign so positive = subject's right = screen right (video
  // is rendered mirrored). Matches the enrol page's convention.
  const yaw = -yawNorm * 90;

  const total = chin.y - foreheadProxy.y;
  const noseProgress = total > 0 ? (noseTip.y - foreheadProxy.y) / total : 0.5;
  // Same multiplier as the enrolment page so down poses are reachable
  // without holding awkwardly.
  const pitch = (noseProgress - 0.55) * 320;
  return { yaw, pitch };
}

function avg(pts: Point[]): Point {
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  const n = Math.max(pts.length, 1);
  return { x: sx / n, y: sy / n };
}

/**
 * Per-frame liveness check. Returns whether the action passed, the
 * status message to show, and a 0..1 progress for the ring.
 *
 * Mutates `state` in place — it's a ref the caller owns, so persisting
 * blink-history across frames doesn't require state plumbing.
 */
function updateLiveness(
  action: FaceAction,
  lms: Point[],
  state: LivenessState,
): { passed: boolean; msg: string; progress: number } {
  if (action === "blink") {
    const ear = avgEAR(lms);
    // Adaptive baseline. The fixed 0.20 / 0.30 thresholds the original
    // code used assumed a Western-eye open EAR of ~0.30, which narrow /
    // mono-lid eyes (extremely common in TW) never reach — baseline
    // open EAR for them sits around 0.18-0.22 and "fully open" simply
    // never crosses 0.22 either, so blinks were uncountable. Instead,
    // track this user's running max EAR as their personal open
    // baseline; closed = 65% of it, open = 85% of it, with absolute
    // caps so a too-loose adaptation can't trigger on micro-jitter.
    if (state.earOpenMax === null) state.earOpenMax = ear;
    // Slow exponential adapt upward only (open-state samples nudge
    // baseline up; closed samples are ignored so a blink doesn't
    // collapse the threshold).
    if (ear > state.earOpenMax) {
      state.earOpenMax = state.earOpenMax * 0.7 + ear * 0.3;
    }
    const openT  = Math.min(0.22, state.earOpenMax * 0.85);
    const closedT = Math.min(0.18, state.earOpenMax * 0.65);
    if (ear < closedT) {
      if (state.blinkLowSince === null) state.blinkLowSince = Date.now();
    } else if (ear > openT && state.blinkLowSince !== null) {
      const lowMs = Date.now() - state.blinkLowSince;
      // 60–500ms of closure looks like a deliberate blink. Anything
      // shorter is noise; longer is "they closed their eyes for a sec"
      // which still counts (don't penalise).
      if (lowMs >= 60) state.blinkCount += 1;
      state.blinkLowSince = null;
    }
    const target = 1;       // one clean blink is enough
    const passed = state.blinkCount >= target;
    const msg = passed
      ? "✓ 偵測到眨眼"
      : `請眨眼一次（已偵測 ${state.blinkCount}/${target}）`;
    return { passed, msg, progress: Math.min(1, state.blinkCount / target) };
  }

  if (action === "smile") {
    const ratio = mouthWidthOverHeight(lms);
    // Relaxed mouth sits around 2.5×; a real smile only needs to push
    // past ~2.6 to be unambiguous. The earlier 3.0 cut-off required an
    // exaggerated grin and felt impossible to trigger for people with
    // smaller mouths / masks tucked under the chin / glasses casting
    // shadow on the upper lip landmarks.
    if (ratio > 2.6) {
      state.smileHoldFrames += 1;
    } else {
      state.smileHoldFrames = Math.max(0, state.smileHoldFrames - 1);
    }
    const target = 6;       // ~0.2s at 30fps
    const passed = state.smileHoldFrames >= target;
    const msg = passed ? "✓ 偵測到微笑"
              : state.smileHoldFrames > 0 ? "請保持微笑…"
              : "請微笑";
    return { passed, msg, progress: Math.min(1, state.smileHoldFrames / target) };
  }

  // turn_left / turn_right
  const { yaw } = estimatePose(lms);
  const wantNeg = action === "turn_left";
  // yaw > 18 = subject turned screen-right; < -18 = screen-left.
  // (estimatePose flips sign for mirror — see comment there.)
  // 25° was a chin-over-shoulder turn; on a phone held arm's-length
  // operators can comfortably hit 18° without losing the camera frame.
  const hit = wantNeg ? yaw < -18 : yaw > 18;
  if (hit) state.turnHoldFrames += 1;
  else state.turnHoldFrames = Math.max(0, state.turnHoldFrames - 1);
  const target = 5;       // ~0.17s at 30fps
  const passed = state.turnHoldFrames >= target;
  const msg = passed ? "✓ 偵測到轉頭"
            : wantNeg ? "請向左轉頭"
            : "請向右轉頭";
  return { passed, msg, progress: Math.min(1, state.turnHoldFrames / target) };
}

function actionInstruction(action: FaceAction): string {
  switch (action) {
    case "blink":      return "請眨眼一次";
    case "smile":      return "請微笑";
    case "turn_left":  return "請向左轉頭";
    case "turn_right": return "請向右轉頭";
  }
}

// Visual indicator of where the operator is in the verification flow.
// Drawn as three connected pills below the camera circle. Each pill
// has one of three states:
//   - done    — green tick, solid fill
//   - active  — accent fill + label visible, pulsing
//   - pending — outline only, muted label
// The connecting lines fill in as steps complete, giving the
// "progress bar" feel without taking up an extra row.
function StepProgress({ stage }: { stage: Stage }): React.ReactElement | null {
  if (stage === "loading" || stage === "failed") return null;
  type StepState = "done" | "active" | "pending";
  const liveness: StepState =
    stage === "liveness" ? "active" :
    "done";
  const frontal: StepState =
    stage === "frontal" ? "active" :
    stage === "submitting" || stage === "success" ? "done" :
    "pending";
  const match: StepState =
    stage === "submitting" ? "active" :
    stage === "success" ? "done" :
    "pending";
  const steps = [
    { key: "liveness", label: "活體驗證", state: liveness },
    { key: "frontal",  label: "正面取像", state: frontal  },
    { key: "match",    label: "比對中",   state: match    },
  ];
  return (
    <div className="w-full max-w-md mt-3 flex items-center justify-between px-2">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className={
              "h-6 w-6 rounded-full flex items-center justify-center text-2xs font-semibold transition-colors " +
              (s.state === "done"
                ? "bg-success text-bg-base"
                : s.state === "active"
                ? "bg-accent text-bg-base ring-2 ring-accent/30"
                : "bg-bg-raised text-text-muted border border-border")
            }>
              {s.state === "done"
                ? <CheckCircle2 className="h-3.5 w-3.5" />
                : i + 1}
            </div>
            <span className={
              "text-2xs transition-colors " +
              (s.state === "active"
                ? "text-accent font-semibold"
                : s.state === "done"
                ? "text-text-secondary"
                : "text-text-muted")
            }>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={
              "flex-1 h-0.5 mx-1 mb-4 rounded-full transition-colors " +
              (s.state === "done" ? "bg-success/60" : "bg-border")
            } />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
